import React, { useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { RewardBannerButtonAction } from '../types/RewardBannerTypes';
import { api } from '../../../../../api';
import { TextField } from '../../../../../ui/_fields/TextField/TextField';
import lookupTitleStyles from '../../../styles/lookupTitle.module.scss';

interface RewardBannerButtonTargetInputProps {
  error?: string;
  buttonAction: RewardBannerButtonAction;
  value: string;
  onChange: (value: string) => void;
  promotionStoryUuid: string | null;
}

type BookLookupResponse = { success: boolean; book: { title: string } | null };

const missingPromotionStoryUuidMessage = 'No story UUID configured on this promotion';

function PromotionStoryTargetDisplay(props: {
  promotionStoryUuid: string | null;
}) {
  const { promotionStoryUuid } = props;
  const storyUuid = promotionStoryUuid?.trim() ?? '';
  const [matchedBookTitle, setMatchedBookTitle] = useState<string | null>(null);
  const lookupSeq = useRef(0);

  useEffect(() => {
    if (storyUuid.length !== 36) {
      setMatchedBookTitle(null);
      return;
    }
    const seq = ++lookupSeq.current;
    const handle = window.setTimeout(() => {
      api
        .get<BookLookupResponse>(`/v1/books/by-uuid/${encodeURIComponent(storyUuid)}`)
        .then((res) => {
          if (lookupSeq.current !== seq) return;
          setMatchedBookTitle(res.data?.book?.title ?? null);
        })
        .catch(() => {
          if (lookupSeq.current !== seq) return;
          setMatchedBookTitle(null);
        });
    }, 300);
    return () => {
      window.clearTimeout(handle);
    };
  }, [storyUuid]);

  const missingStoryUuid = !storyUuid;

  return (
    <Form.Group>
      <Form.Label>Story UUID</Form.Label>
      {missingStoryUuid ? (
        <div className="text-danger small mt-1">{missingPromotionStoryUuidMessage}</div>
      ) : (
        <>
          <div className="font-monospace small">{storyUuid}</div>
          {matchedBookTitle ? (
            <div className={`small mt-1 ${lookupTitleStyles.title}`}>{matchedBookTitle}</div>
          ) : null}
        </>
      )}
    </Form.Group>
  );
}

export function RewardBannerButtonTargetInput(props: RewardBannerButtonTargetInputProps) {
  const {
    buttonAction,
    value,
    onChange,
    error,
    promotionStoryUuid,
  } = props;

  if (buttonAction === RewardBannerButtonAction.Story) {
    return (
      <PromotionStoryTargetDisplay
        promotionStoryUuid={promotionStoryUuid}
      />
    );
  }

  let channelId = '';
  try {
    channelId = JSON.parse(value).channelId;
  } catch {
    channelId = '';
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(JSON.stringify({
      channelId: event.target.value,
    }));
  };

  return (
    <TextField
      label="Channel Id"
      name="channelId"
      placeholder="Channel id (ie announcements, new_releases, arcana_quest_info)"
      value={channelId}
      onChange={handleChange}
      error={error}
    />
  );
}
